import { eEscrowWireDisbursementCategories } from 'components/order/escrow/disbursements/achPushDisbursements/createWireFromACH/enums/eEscrowWireDisbursementCategories';
import { eManualCheckDisbursementCategory } from 'types/accounting/eManualCheckDisbursementCategory';
import { eInternalTransferCategory } from 'types/accounting/response/eInternalTransferCategory';
import { eAccountType } from 'types/order/escrow/IAchAccountData';
import { eACHDisbursementCategories } from 'types/order/escrow/eEscrowAchPushDisbursementCategories';
import * as yup from 'yup';
import { format, addDays } from 'date-fns';

const alphaNumericSpaceUnderscoreAndDashAndSlashRE: RegExp = /^[a-zA-Z0-9,/_\- ]+$/;
const alphaNumericRegex: RegExp = /^[a-zA-Z0-9]+$/;

const amountCents = yup
  .number()
  .typeError('Amount must be a number')
  .positive('Amount must be greater than 0')
  .required('Amount must be greater than 0')
  .nullable();

const memo = yup
  .string()
  .max(160, 'Memo must be less than 160 characters')
  .matches(alphaNumericSpaceUnderscoreAndDashAndSlashRE, 'Invalid characters')
  .notRequired()
  .nullable();

const achMemo = yup
  .string()
  .max(11, 'Memo must be less than 11 characters')
  .matches(alphaNumericRegex, 'Invalid characters')
  .required()
  .nullable();

const routingNumber = yup
  .string()
  .required('Routing number is required')
  .test('lengthCheck', 'Routing number is the wrong length (should be 9 characters)', (rtn) => rtn?.length === 9)
  .nullable()
  .typeError('Routing number is required');

const accountNumber = yup
  .string()
  .min(5, 'Account number is too short (minimum is 5 characters)')
  .required('Account number is required')
  .nullable();

const accountNumberConfirmation = yup
  .string()
  .test(
    'Account numbers must match',
    'Account numbers do not match',
    (accountNumberConfirmation, testContext) => accountNumberConfirmation === testContext.parent.accountNumber
  )
  .nullable();

const achAccountValidation = yup.object({
  name: yup.string().required('Name is required').nullable(),
  routingNumber,
  accountNumber,
  accountNumberConfirmation,
  accountType: yup.mixed<eAccountType>().nullable().required(),
});

const beneficiary = yup.string().required('Beneficiary is required').nullable();
const mailingAddress = yup.object({
  streetAddress: yup.string().required('Street Address is required').nullable(),
  city: yup.string().required('City is required').nullable(),
  stateAbbreviation: yup.string().required('State is required').nullable(),
  zipCode: yup.string().required('Zip Code is required').min(5, 'Enter ${min} digits').nullable(),
});

const issuanceDate = yup
  .string()
  .nullable()
  .test('issuance-date-range', 'Issuance date must be between today and 7 days from today', function (value) {
    if (!value) return true; // Allow empty values

    // Only validate if the value has been modified from the original
    const context = this.options.context;
    const originalValue = context && context.originalValue && context.originalValue.issuanceDate;

    if (originalValue === value) {
      return true; // Skip validation if value hasn't changed
    }

    const today = format(new Date(), 'yyyy-MM-dd');
    const maxDate = format(addDays(new Date(), 7), 'yyyy-MM-dd');

    // Check if value is a valid date string
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return this.createError({
        path: this.path,
        message: 'Invalid date format',
      });
    }

    if (value < today) {
      return this.createError({
        path: this.path,
        message: 'Issuance date cannot be earlier than today',
      });
    }

    if (value > maxDate) {
      return this.createError({
        path: this.path,
        message: 'Issuance date cannot be more than 7 days from today',
      });
    }

    return true;
  });

const schemas = {
  Check: {
    amountCents,
    memo,
    name: beneficiary,
    category: yup.mixed<eManualCheckDisbursementCategory>().nullable().required(),
    mailingAddress,
    issuanceDate,
  },
  Wire: {
    amountCents,
    beneficiary,
    memo,
    routingNumber,
    accountNumber,
    accountNumberConfirmation,
    category: yup.mixed<eEscrowWireDisbursementCategories>().nullable().required(),
  },
  ACH: {
    amountCents,
    wellsFargoTransactionNumber: achMemo,
    category: yup.mixed<eACHDisbursementCategories>().nullable().required(),
    account: achAccountValidation,
  },
  'Internal Transfer': {
    amountCents,
    category: yup.mixed<eInternalTransferCategory>().nullable().required(),
    destinationAccountId: yup.string().required().nullable(),
  },
};

const HoldbackSchema = {
  holdbackProperties: yup.object({
    reason: yup.string().required().nullable(),
    dueDate: yup.string().required().nullable(),
    otherReason: yup
      .string()
      .when('reason', {
        is: 'Other Holdback',
        then: yup.string().required('Other reason is required').typeError('Other reason is required'),
      })
      .nullable(),
  }),
  issuanceDate: yup.string().nullable().notRequired(),
};

/** Yups documentation and how it actually works are at odds. The function needed has each parameter like it is here, not in an array like the docs. */
const calculateParams = ['type', 'isDeleting', 'holdback'];
const calculateSchema = (key: string): any => {
  return (type: string, isDeleting: boolean, holdback: boolean) => {
    const schemaToUse = (schemas as any)[type][key];
    if (holdback) {
      return (HoldbackSchema as any)[key] ?? yup.mixed().nullable().notRequired();
    }
    if (isDeleting || !schemaToUse) {
      return yup.mixed().nullable().notRequired();
    }
    return schemaToUse;
  };
};

export const disbursementSchema = yup.object({
  amountCents: yup.number().when(calculateParams, calculateSchema('amountCents')),
  memo: yup.string().when(calculateParams, calculateSchema('memo')),
  routingNumber: yup.string().when(calculateParams, calculateSchema('routingNumber')),
  account: yup.object().when(calculateParams, calculateSchema('account')),
  accountNumber: yup.string().when(calculateParams, calculateSchema('accountNumber')),
  accountNumberConfirmation: yup.string().when(calculateParams, calculateSchema('accountNumberConfirmation')),
  name: yup.string().when(calculateParams, calculateSchema('name')),
  beneficiary: yup.string().when(calculateParams, calculateSchema('beneficiary')),
  category: yup.mixed<any>().when(calculateParams, calculateSchema('category')),
  mailingAddress: yup.object().when(calculateParams, calculateSchema('mailingAddress')),
  wellsFargoTransactionNumber: yup.string().when(calculateParams, calculateSchema('wellsFargoTransactionNumber')),
  destinationAccountId: yup.string().when(calculateParams, calculateSchema('destinationAccountId')),
  trackingNumber: yup.string().when(calculateParams, calculateSchema('trackingNumber')),
  shipmentType: yup.string().when(calculateParams, calculateSchema('shipmentType')),
  holdbackProperties: yup.object().when(calculateParams, calculateSchema('holdbackProperties')),
  issuanceDate: yup.string().when(calculateParams, calculateSchema('issuanceDate')),
});
